import React, { useState, useCallback, useEffect } from 'react';
import { connect, useSelector } from 'react-redux';
import { createStructuredSelector } from 'reselect';
import { getUI, getFilters } from 'redux/selectors';
import { updateUI } from 'redux/actions/ui';
import './JournalChaptersDialog.css';

import * as JournalActions from 'redux/actions/journal';
import iconCalendar from 'assets/images/icons_svg/icon_mission_calendar.svg';
import iconEdit from 'assets/images/icons_svg/icon_edit.svg';
import iconDelete from 'assets/images/icons_svg/icon_delete.svg';

const TITLE_MAX = 50;

const formatDisplayDate = (dateStr) => {
    if (!dateStr) {
        return '';
    }
    const d = new Date(`${dateStr}T00:00:00`);
    return d.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'long',
        day: 'numeric'
    });
};

const formatDisplayDateRange = (startStr, endStr) => {
    const startDisplay = formatDisplayDate(startStr);
    const endDisplay = formatDisplayDate(endStr);
    if (!startDisplay && !endDisplay) {
        return '';
    }
    if (!startDisplay) {
        return endDisplay;
    }
    if (!endDisplay) {
        return startDisplay;
    }
    return `${startDisplay} – ${endDisplay}`;
};

const JournalChaptersDialog = ({ close, dispatch, journal: journalProp }) => {
    const filters = useSelector(getFilters);
    const activeJournal =
        (filters?.filterByJournal && filters.filterByJournal[0]) || null;
    const journal = journalProp || activeJournal;

    const [chapters, setChapters] = useState([]);
    const [chapterTitle, setChapterTitle] = useState('');
    const [startDate, setStartDate] = useState('');
    const [endDate, setEndDate] = useState('');
    const [editingIndex, setEditingIndex] = useState(null);
    const [editTitle, setEditTitle] = useState('');
    const [editStartDate, setEditStartDate] = useState('');
    const [editEndDate, setEditEndDate] = useState('');
    const [isSaving, setIsSaving] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [saveError, setSaveError] = useState(null);
    const [isDeleting, setIsDeleting] = useState(null); // index being deleted
    const [isPatching, setIsPatching] = useState(false);

    const missionStartDate = journal?.mission_start_date
        ? journal.mission_start_date.split('T')[0]
        : '';
    const missionStartDateLabel = missionStartDate
        ? formatDisplayDate(missionStartDate)
        : '';

    const canAdd =
        chapterTitle.trim() !== '' && startDate !== '' && endDate !== '';

    const journalId = journal?.id;

    useEffect(() => {
        if (!journalId) {
            return;
        }
        setIsLoading(true);
        dispatch(JournalActions.fetchChapters(journalId))
            .then((res) => {
                const results = res?.payload?.data?.results || [];
                const loaded = results.map((ch) => ({
                    title: ch.chapter_name,
                    startDate: ch.start_date,
                    endDate: ch.end_date,
                    apiId: ch.id
                }));
                setChapters(loaded);
                setIsLoading(false);
            })
            .catch(() => {
                setIsLoading(false);
            });
    }, [journalId, dispatch]);

    const _close = useCallback(() => {
        if (close) {
            close();
        } else {
            dispatch(updateUI({ dialogs: [] }));
        }
    }, [close, dispatch]);

    const getChapterValidationError = useCallback(
        (candidateStart, candidateEnd, ignoreIndex = null) => {
            if (!candidateStart || !candidateEnd) {
                return null;
            }

            if (candidateStart > candidateEnd) {
                return 'Chapter start date must be on or before the end date.';
            }

            if (missionStartDate && candidateStart < missionStartDate) {
                return `Please select a chapter date within the Mission timeline. The date must be on or after ${missionStartDateLabel}.`;
            }

            const overlaps = chapters.some((ch, idx) => {
                if (idx === ignoreIndex) {
                    return false;
                }
                if (!ch.startDate || !ch.endDate) {
                    return false;
                }
                return (
                    candidateStart <= ch.endDate && ch.startDate <= candidateEnd
                );
            });

            if (overlaps) {
                return 'Selected chapter dates conflict with an existing chapter. Please choose a different date range.';
            }

            return null;
        },
        [chapters, missionStartDate, missionStartDateLabel]
    );

    const handleAdd = useCallback(() => {
        if (!canAdd) {
            return;
        }

        const validationError = getChapterValidationError(startDate, endDate);
        if (validationError) {
            setSaveError(validationError);
            return;
        }

        setChapters((prev) => [
            ...prev,
            { title: chapterTitle.trim(), startDate, endDate }
        ]);
        setChapterTitle('');
        setStartDate('');
        setEndDate('');
        setSaveError(null);
    }, [canAdd, chapterTitle, startDate, endDate, getChapterValidationError]);

    const startEdit = useCallback(
        (index) => {
            setEditingIndex(index);
            setEditTitle(chapters[index].title);
            setEditStartDate(chapters[index].startDate);
            setEditEndDate(chapters[index].endDate);
        },
        [chapters]
    );

    const confirmEdit = useCallback(
        (index) => {
            if (!editTitle.trim()) {
                return;
            }
            const chapter = chapters[index];

            // If chapter exists on server, PATCH it
            if (chapter.apiId && journalId) {
                setIsPatching(true);
                dispatch(
                    JournalActions.patchChapter(journalId, chapter.apiId, {
                        chapter_name: editTitle.trim()
                        // start_date and end_date are immutable after creation
                    })
                )
                    .then(() => {
                        setChapters((prev) => {
                            const updated = [...prev];
                            updated[index] = {
                                ...chapter,
                                title: editTitle.trim()
                            };
                            return updated;
                        });
                        setEditingIndex(null);
                        setIsPatching(false);
                    })
                    .catch((err) => {
                        setIsPatching(false);
                        const errMsg =
                            err?.error?.response?.data?.error ||
                            err?.response?.data?.error ||
                            'Failed to update chapter. Please try again.';
                        setSaveError(errMsg);
                    });
            } else {
                // New (not yet saved) chapter — just update local state
                if (!editStartDate || !editEndDate) {
                    return;
                } // Prevent local updates missing dates

                const validationError = getChapterValidationError(
                    editStartDate,
                    editEndDate,
                    index
                );
                if (validationError) {
                    setSaveError(validationError);
                    return;
                }

                setChapters((prev) => {
                    const updated = [...prev];
                    updated[index] = {
                        title: editTitle.trim(),
                        startDate: editStartDate,
                        endDate: editEndDate,
                        apiId: chapter.apiId
                    };
                    return updated;
                });
                setEditingIndex(null);
                setSaveError(null);
            }
        },
        [
            editTitle,
            editStartDate,
            editEndDate,
            chapters,
            journalId,
            dispatch,
            getChapterValidationError
        ]
    );

    const cancelEdit = useCallback(() => setEditingIndex(null), []);

    const handleDelete = useCallback(
        (index) => {
            const chapter = chapters[index];

            // If chapter exists on server, DELETE it via API
            if (chapter.apiId && journalId) {
                setIsDeleting(index);
                dispatch(JournalActions.deleteChapter(journalId, chapter.apiId))
                    .then(() => {
                        setChapters((prev) =>
                            prev.filter((_, i) => i !== index)
                        );
                        setIsDeleting(null);
                        if (editingIndex === index) {
                            setEditingIndex(null);
                        }
                    })
                    .catch(() => {
                        setIsDeleting(null);
                        setSaveError(
                            'Failed to delete chapter. Please try again.'
                        );
                    });
            } else {
                // New (unsaved) chapter — just remove from local state
                setChapters((prev) => prev.filter((_, i) => i !== index));
                if (editingIndex === index) {
                    setEditingIndex(null);
                }
            }
        },
        [editingIndex, chapters, journalId, dispatch]
    );

    const handleSave = () => {
        if (!journal?.id) {
            setSaveError(
                'No active journal found. Please close and try again.'
            );
            return;
        }

        // Only POST chapters that are newly added (no apiId yet).
        // Existing chapters were already updated via PATCH / deleted via DELETE.
        const newChapters = chapters
            .filter((ch) => !ch.apiId)
            .map((ch) => ({
                chapter_name: ch.title,
                start_date: ch.startDate,
                end_date: ch.endDate
            }));

        if (newChapters.length === 0) {
            _close();
            return;
        }

        setIsSaving(true);
        setSaveError(null);

        dispatch(JournalActions.saveChapters(journal.id, newChapters))
            .then(() => {
                setIsSaving(false);
                _close();
            })
            .catch((err) => {
                console.error('[JournalChapters] Save failed:', err);
                setIsSaving(false);
                const errMsg =
                    err?.error?.response?.data?.error ||
                    err?.response?.data?.error ||
                    'Failed to save. Please try again.';
                setSaveError(errMsg);
            });
    };

    return (
        <div
            id="journal-chapters-modal"
            className="modal-frame centered-modal-frame"
        >
            <div className="modal-box jc-modal-box">
                <div className="jc-header">
                    <h2 className="jc-title">Journal Chapters</h2>
                    <p className="jc-subtitle">
                        Your journal can be divided into sections based on the
                        various locations that you traveled or key points of
                        your mission such as the time before your mission, the
                        MTC, mission areas, and your time shortly after you have
                        returned.
                    </p>
                </div>

                <div className="jc-input-row">
                    <div className="jc-field jc-field--title">
                        <div className="jc-label-row">
                            <label className="jc-label">Chapter Title</label>
                            <span className="jc-char-count">
                                {chapterTitle.length}/{TITLE_MAX}
                            </span>
                        </div>
                        <input
                            type="text"
                            className="jc-input"
                            placeholder="e.g., MTC, Mission Area Name, Country and City"
                            maxLength={TITLE_MAX}
                            value={chapterTitle}
                            onChange={(e) => setChapterTitle(e.target.value)}
                        />
                    </div>

                    <div className="jc-field jc-field--date">
                        <label className="jc-label">Start Date</label>
                        <div className="jc-date-wrapper">
                            <img
                                src={iconCalendar}
                                alt=""
                                className="jc-date-icon"
                            />
                            <input
                                type="date"
                                className={`jc-input jc-input--date${
                                    !startDate ? ' jc-input--date-empty' : ''
                                }`}
                                value={startDate}
                                onChange={(e) => setStartDate(e.target.value)}
                            />
                            {!startDate && (
                                <span className="jc-date-placeholder">
                                    00/00/0000
                                </span>
                            )}
                        </div>
                    </div>

                    <div className="jc-field jc-field--date">
                        <label className="jc-label">End Date</label>
                        <div className="jc-date-wrapper">
                            <img
                                src={iconCalendar}
                                alt=""
                                className="jc-date-icon"
                            />
                            <input
                                type="date"
                                className={`jc-input jc-input--date${
                                    !endDate ? ' jc-input--date-empty' : ''
                                }`}
                                value={endDate}
                                onChange={(e) => setEndDate(e.target.value)}
                            />
                            {!endDate && (
                                <span className="jc-date-placeholder">
                                    00/00/0000
                                </span>
                            )}
                        </div>
                    </div>

                    <button
                        className="btn btn-gradient jc-add-btn-text"
                        onClick={handleAdd}
                        disabled={!canAdd}
                        title="Add chapter"
                    >
                        Add
                    </button>
                </div>

                <div className="jc-scroll-area">
                    {isLoading ? (
                        <div className="jc-loading">Loading chapters…</div>
                    ) : chapters.length > 0 ? (
                        <div className="jc-list">
                            {chapters.map((ch, index) => (
                                <div
                                    key={ch.apiId ?? `new-${index}`}
                                    className="jc-list-item"
                                >
                                    {editingIndex === index ? (
                                        <div className="jc-edit-row">
                                            <div className="jc-edit-fields">
                                                <input
                                                    type="text"
                                                    className="jc-input jc-input--inline"
                                                    maxLength={TITLE_MAX}
                                                    value={editTitle}
                                                    onChange={(e) =>
                                                        setEditTitle(
                                                            e.target.value
                                                        )
                                                    }
                                                />
                                                {!chapters[index].apiId ? (
                                                    <>
                                                        <div className="jc-date-wrapper jc-date-wrapper--inline">
                                                            <img
                                                                src={
                                                                    iconCalendar
                                                                }
                                                                alt=""
                                                                className="jc-date-icon"
                                                            />
                                                            <input
                                                                type="date"
                                                                className="jc-input jc-input--date jc-input--inline"
                                                                value={
                                                                    editStartDate
                                                                }
                                                                onChange={(e) =>
                                                                    setEditStartDate(
                                                                        e.target
                                                                            .value
                                                                    )
                                                                }
                                                            />
                                                        </div>
                                                        <div className="jc-date-wrapper jc-date-wrapper--inline">
                                                            <img
                                                                src={
                                                                    iconCalendar
                                                                }
                                                                alt=""
                                                                className="jc-date-icon"
                                                            />
                                                            <input
                                                                type="date"
                                                                className="jc-input jc-input--date jc-input--inline"
                                                                value={
                                                                    editEndDate
                                                                }
                                                                onChange={(e) =>
                                                                    setEditEndDate(
                                                                        e.target
                                                                            .value
                                                                    )
                                                                }
                                                            />
                                                        </div>
                                                    </>
                                                ) : (
                                                    <div
                                                        className="jc-date-wrapper jc-date-wrapper--inline"
                                                        style={{
                                                            backgroundColor:
                                                                'transparent',
                                                            padding: '0 8px',
                                                            color: '#888'
                                                        }}
                                                    >
                                                        {formatDisplayDateRange(
                                                            chapters[index]
                                                                .startDate,
                                                            chapters[index]
                                                                .endDate
                                                        )}
                                                    </div>
                                                )}
                                            </div>
                                            <div className="jc-item-actions">
                                                <button
                                                    className="jc-action-btn jc-save-inline-btn"
                                                    onClick={() =>
                                                        confirmEdit(index)
                                                    }
                                                    disabled={isPatching}
                                                    title="Save"
                                                >
                                                    {isPatching ? '…' : '✓'}
                                                </button>
                                                <button
                                                    className="jc-action-btn jc-cancel-inline-btn"
                                                    onClick={cancelEdit}
                                                    disabled={isPatching}
                                                    title="Cancel"
                                                >
                                                    ✕
                                                </button>
                                            </div>
                                        </div>
                                    ) : (
                                        <>
                                            <div className="jc-item-info">
                                                <span className="jc-item-title">
                                                    {ch.title}
                                                </span>
                                                <span className="jc-item-date">
                                                    {formatDisplayDateRange(
                                                        ch.startDate,
                                                        ch.endDate
                                                    )}
                                                </span>
                                                {ch.merged_from_display_name && (
                                                    <span className="jc-item-merged">
                                                        Merged from:{' '}
                                                        {
                                                            ch.merged_from_display_name
                                                        }
                                                    </span>
                                                )}
                                            </div>
                                            <div className="jc-item-actions">
                                                <button
                                                    className="jc-action-btn"
                                                    onClick={() =>
                                                        startEdit(index)
                                                    }
                                                    title="Edit"
                                                >
                                                    <img
                                                        src={iconEdit}
                                                        alt="Edit"
                                                        className="jc-action-icon"
                                                    />
                                                </button>
                                                <button
                                                    className="jc-action-btn jc-action-btn--delete"
                                                    onClick={() =>
                                                        handleDelete(index)
                                                    }
                                                    disabled={
                                                        isDeleting === index
                                                    }
                                                    title="Delete"
                                                >
                                                    {isDeleting === index ? (
                                                        <span className="jc-deleting-indicator">
                                                            …
                                                        </span>
                                                    ) : (
                                                        <img
                                                            src={iconDelete}
                                                            alt="Delete"
                                                            className="jc-action-icon"
                                                        />
                                                    )}
                                                </button>
                                            </div>
                                        </>
                                    )}
                                </div>
                            ))}
                        </div>
                    ) : null}
                </div>

                <div className="jc-footer">
                    {saveError && (
                        <span className="jc-save-error">{saveError}</span>
                    )}
                    <div className="jc-footer-actions">
                        <button
                            className="jc-btn-cancel"
                            onClick={_close}
                            disabled={isSaving}
                        >
                            Cancel
                        </button>
                        <button
                            className="btn btn-gradient jc-btn-save"
                            onClick={handleSave}
                            disabled={isSaving}
                        >
                            {isSaving ? 'Saving...' : 'Save & Close'}
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};

const state = createStructuredSelector({ ui: getUI });
export default connect(state)(JournalChaptersDialog);
